import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
  TemplateRef,
  effect,
  inject,
  signal,
  untracked,
  viewChildren
} from '@angular/core';
import {
  Middleware,
  Placement,
  autoPlacement,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift
} from '@floating-ui/dom';

import { closestDropdownPanel } from './dropdown-panel.utils';

export type DropdownMiddleware = 'flip' | 'shift' | 'autoPlacement' | 'offset';

export interface DropdownOptions {
  position?: Placement;
  middlewares?: DropdownMiddleware[];
  maxWidth?: number;
  minWidth?: number;
  /**
   * Exact panel width, overriding the content's natural width. Use it to pin a
   * panel to its trigger (a grid cell editor must not spill over neighbouring
   * columns); leave it unset for menus that should size to their content.
   */
  width?: number;
  maxHeight?: number;
  minHeight?: number;
  goUpperTarget?: boolean;
  offset?: number;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  context?: unknown;
  useDefaultStyles?: boolean;
  onClose?: () => void;
}

export interface DropdownHandle {
  readonly id: number;
}

interface DropdownEntry {
  id: number;
  target: HTMLElement;
  template: TemplateRef<unknown>;
  context: Record<string, unknown>;
  opts: DropdownOptions;
  /** Set once floating-ui has placed the panel, so it never flashes at (0, 0). */
  visible: boolean;
  /** Kept mounted while fading out; ignored by every stack operation. */
  closing: boolean;
}

/** Must outlast the opacity transition applied to default-styled panels. */
const FADE_OUT_MS = 400;

@Component({
  selector: 'app-dropdown-singleton',
  imports: [NgTemplateOutlet],
  templateUrl: './dropdown-singleton.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class DropdownSingletonComponent implements OnDestroy {
  private readonly panelRefs = viewChildren<ElementRef<HTMLDivElement>>('panel');
  private readonly renderer = inject(Renderer2);

  /** The open stack, deepest first, plus any entries still fading out. */
  protected readonly entries = signal<DropdownEntry[]>([]);

  private readonly panels = new Map<number, HTMLDivElement>();
  private readonly autoUpdates = new Map<number, () => void>();
  private readonly defaultMiddlewares: DropdownMiddleware[] = ['offset', 'flip', 'shift'];
  private documentClickListener?: () => void;
  private documentKeyListener?: () => void;
  private lastId = 0;

  constructor() {
    effect(() => {
      for (const ref of this.panelRefs()) {
        const panel = ref.nativeElement;
        const id = Number(panel.dataset['dropdownId']);
        if (!this.panels.has(id)) {
          this.panels.set(id, panel);
          untracked(() => this.startTracking(id, panel));
        }
      }
    });
  }

  ngOnDestroy(): void {
    for (const cleanup of this.autoUpdates.values()) {
      cleanup();
    }
    this.autoUpdates.clear();
    this.panels.clear();
    this.detachDocumentListeners();
  }

  show(
    target: HTMLElement,
    template: TemplateRef<unknown>,
    opts: DropdownOptions = {}
  ): DropdownHandle {
    const open = this.openEntries();
    const existing = open.find((entry) => entry.target === target);
    if (existing) {
      return { id: existing.id };
    }

    // A dropdown whose target lives inside an open panel stacks on top of it.
    // Anything else is a sibling and replaces whatever is currently open, since
    // triggers that stop propagation leave the click-outside walk no chance to.
    const parentIndex = this.hostEntryIndex(open, target);
    for (let i = open.length - 1; i > parentIndex; i--) {
      this.closeEntry(open[i].id);
    }

    const id = ++this.lastId;
    this.entries.update((list) => [
      ...list,
      {
        id,
        target,
        template,
        context: (opts.context ?? {}) as Record<string, unknown>,
        opts,
        visible: false,
        closing: false
      }
    ]);
    this.attachDocumentListeners();

    return { id };
  }

  /** Closes the given dropdown and everything stacked above it, or the top one. */
  close(handle?: DropdownHandle): void {
    const open = this.openEntries();
    const index = handle
      ? open.findIndex((entry) => entry.id === handle.id)
      : open.length - 1;
    if (index < 0) {
      return;
    }
    for (let i = open.length - 1; i >= index; i--) {
      this.closeEntry(open[i].id);
    }
  }

  isOpen(): boolean {
    return this.openEntries().length > 0;
  }

  protected panelClasses(entry: DropdownEntry): string {
    const base = 'absolute top-0 left-0 z-50';

    if (entry.opts.useDefaultStyles ?? true) {
      return `${base} flex min-w-max flex-col rounded-md bg-elevated border border-default p-1 text-xs font-normal shadow-md transition-opacity duration-base`;
    }

    return base;
  }

  /**
   * Panels swallow clicks, so the document listener never sees them; run the
   * same walk here to drop any dropdown stacked above the clicked panel.
   */
  protected onPanelClick(event: MouseEvent): void {
    this.closeAboveClick(event.target as Node);
    event.stopPropagation();
  }

  private openEntries(): DropdownEntry[] {
    return this.entries().filter((entry) => !entry.closing);
  }

  private closeEntry(id: number): void {
    const entry = this.entries().find((item) => item.id === id && !item.closing);
    if (!entry) {
      return;
    }

    // Stop repositioning before the fade-out so the panel does not drift away.
    this.autoUpdates.get(id)?.();
    this.autoUpdates.delete(id);

    this.entries.update((list) =>
      list.map((item) =>
        item.id === id ? { ...item, closing: true, visible: false } : item
      )
    );

    if (this.openEntries().length === 0) {
      this.detachDocumentListeners();
    }

    entry.opts.onClose?.();

    setTimeout(() => {
      this.panels.delete(id);
      this.entries.update((list) => list.filter((item) => item.id !== id));
    }, FADE_OUT_MS);
  }

  private startTracking(id: number, panel: HTMLDivElement): void {
    const entry = this.entries().find((item) => item.id === id);
    if (!entry) {
      return;
    }

    const names =
      (entry.opts.middlewares ?? []).length > 0
        ? (entry.opts.middlewares as DropdownMiddleware[])
        : this.defaultMiddlewares;
    const middleware = names.map((name) => this.getMiddleware(name, entry.opts));

    this.autoUpdates.set(
      id,
      autoUpdate(entry.target, panel, () => {
        computePosition(entry.target, panel, {
          placement: entry.opts.position,
          middleware,
          strategy: 'fixed'
        }).then(({ x, y }) => {
          Object.assign(panel.style, { left: `${x}px`, top: `${y}px` });
          this.reveal(id);
        });
      })
    );
  }

  private reveal(id: number): void {
    if (!this.entries().some((item) => item.id === id && !item.visible && !item.closing)) {
      return;
    }
    this.entries.update((list) =>
      list.map((item) => (item.id === id ? { ...item, visible: true } : item))
    );
  }

  private closeAboveClick(clicked: Node): void {
    const open = this.openEntries();
    const hostIndex = this.hostEntryIndex(open, clicked);
    for (let i = open.length - 1; i > hostIndex; i--) {
      const entry = open[i];
      if (entry.target.contains(clicked)) {
        return;
      }
      if (!(entry.opts.closeOnClickOutside ?? true)) {
        return;
      }
      this.closeEntry(entry.id);
    }
  }

  private hostEntryIndex(open: DropdownEntry[], node: Node): number {
    const panel = closestDropdownPanel(node);
    if (!panel) {
      return -1;
    }
    const id = Number(panel.dataset['dropdownId']);
    return open.findIndex((entry) => entry.id === id);
  }

  private attachDocumentListeners(): void {
    if (this.documentKeyListener) {
      return;
    }

    this.documentKeyListener = this.renderer.listen(
      'document',
      'keydown',
      (event: KeyboardEvent) => {
        if (event.key !== 'Escape') {
          return;
        }
        const open = this.openEntries();
        const top = open[open.length - 1];
        if (top && (top.opts.closeOnEscape ?? true)) {
          this.closeEntry(top.id);
        }
      }
    );

    // Deferred so the click that opened the dropdown does not close it again.
    setTimeout(() => {
      if (this.documentClickListener || this.openEntries().length === 0) {
        return;
      }
      this.documentClickListener = this.renderer.listen(
        'document',
        'click',
        (event: MouseEvent) => this.closeAboveClick(event.target as Node)
      );
    }, 0);
  }

  private detachDocumentListeners(): void {
    this.documentClickListener?.();
    this.documentClickListener = undefined;
    this.documentKeyListener?.();
    this.documentKeyListener = undefined;
  }

  private getMiddleware(middleware: DropdownMiddleware, opts: DropdownOptions): Middleware {
    switch (middleware) {
      case 'flip':
        return flip();
      case 'shift':
        return shift({ padding: 5 });
      case 'autoPlacement':
        return autoPlacement();
      case 'offset':
        if (opts.goUpperTarget) {
          return offset(({ rects }) => {
            return -rects.reference.height - (opts.offset ?? 8);
          });
        }
        return offset(opts.offset ?? 8);
    }
  }
}
